import { useNavigate } from 'react-router';
import { decodeToken } from '@/helpers/tokenHelpers';
import { AccessTokenType, AuthResponseType } from '@/types/sharedTypes';
import { authTokenService } from '@/bundle/shared/services/authTokenService';
import { sessionStorageService } from '@/helpers/storageHelpers';
import { REDIRECT_URL_KEY } from '@/const/shared';
import { USER_ROLE_MAP, UserRoleType } from '@/const/user';
import { getDashboardRedirectPath } from '../LoginFlow/LoginPage/const/const';
import { getFirstRole, isMasterAdminRole, setStorageCurrentRole } from '@/helpers/userHelpers';
import { useQueryClient } from '@tanstack/react-query';
import { loadSelfUserDetails } from '@/bundle/shared/api';
import { getLoginPath } from '../LoginFlow/LoginPage/path/path';
import { showSnackbarResponseError } from '@/helpers/helpers';
import { hasBillingSubscription, isStandardPlanType } from '@/helpers/billingHelpers';

export const useLogin = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const login = async (authResponse: AuthResponseType, currentRole?: UserRoleType) => {
    const { access, refresh } = authResponse;
    const { user_id, active_roles } = decodeToken<AccessTokenType>(access);
    const token = { access, refresh, user_id };
    const previousLocation = sessionStorageService.get(REDIRECT_URL_KEY);

    const role = currentRole || getFirstRole(active_roles);

    authTokenService.setTokens(token);
    setStorageCurrentRole(role);

    // [Master Admin check] Load user details for "master_admin" and check if user required to finish checkout flow
    if (role === USER_ROLE_MAP.MASTER_ADMIN) {
      const loginUrl = getLoginPath();

      try {
        const userResponse = await queryClient.fetchQuery({
          queryKey: ['load_self_user'],
          queryFn: () => loadSelfUserDetails(),
        });

        if (userResponse.error) {
          showSnackbarResponseError(userResponse?.error);

          return navigate(loginUrl);
        }

        const userDetails = userResponse?.body;
        const isMasterAdmin = isMasterAdminRole(userDetails);
        const isStandardPlan = isStandardPlanType(userDetails);
        const hasSubscription = hasBillingSubscription(userDetails);

        // Redirect master admin to dashboard in trial mode.
        if (isMasterAdmin && isStandardPlan && !hasSubscription) {
          return navigate(getDashboardRedirectPath(role), { state: { showTrialModeModal: true } });
        }
      } catch (error) {
        return navigate(loginUrl);
      }
    }
    // [End Master Admin check]

    if (previousLocation) {
      sessionStorageService.remove(REDIRECT_URL_KEY);

      return navigate(previousLocation, { replace: true });
    }

    const dashboardUrl = getDashboardRedirectPath(role);

    return navigate(dashboardUrl, { replace: true });
  };

  return { login };
};
